import React, { useState, useCallback } from 'react';
import { WindowProvider, useWindows } from './contexts/WindowContext';
import { RoutingProvider, useRouting } from './contexts/RoutingContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { SoundProvider } from './contexts/SoundContext';
import { ContextMenuProvider, useContextMenu } from './contexts/ContextMenuContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { DesktopIconsProvider } from './contexts/DesktopIconsContext';
import { LoginScreen } from './components/Login';
import { BootScreen } from './components/Boot';
import { Desktop } from './components/Desktop';
import { Taskbar } from './components/Taskbar';
import { Window } from './components/Window';
import { CRTEffect } from './components/Effects';
import { ContextMenu } from './components/ContextMenu';
import {
  MyComputer,
  Projects,
  AboutMe,
  Contact,
  Resume,
  Blog,
  Settings,
  RecycleBin,
  Notepad,
  Minesweeper,
  Solitaire,
  InternetExplorer,
  MSNMessenger,
  CommandPrompt,
  Minecraft,
  Research,
} from './components/Apps';
import type { AppType } from './types';
import './styles/global.css';

// App content renderer
const AppContent: React.FC<{ appType: AppType }> = ({ appType }) => {
  switch (appType) {
    case 'my-computer':
      return <MyComputer />;
    case 'projects':
      return <Projects />;
    case 'about-me':
      return <AboutMe />;
    case 'contact':
      return <Contact />;
    case 'resume':
      return <Resume />;
    case 'blog':
      return <Blog />;
    case 'settings':
      return <Settings />;
    case 'recycle-bin':
      return <RecycleBin />;
    case 'notepad':
      return <Notepad />;
    case 'minesweeper':
      return <Minesweeper />;
    case 'solitaire':
      return <Solitaire />;
    case 'internet-explorer':
      return <InternetExplorer />;
    case 'msn-messenger':
      return <MSNMessenger />;
    case 'command-prompt':
      return <CommandPrompt />;
    case 'minecraft':
      return <Minecraft />;
    case 'research':
      return <Research />;
    default:
      return <div style={{ padding: 16 }}>App not found</div>;
  }
};

// Window Manager component
const WindowManager: React.FC = () => {
  const { windows } = useWindows();

  return (
    <>
      {windows.map((window) => (
        <Window key={window.id} config={window}>
          <AppContent appType={window.component} />
        </Window>
      ))}
    </>
  );
};

// Context Menu Renderer
const ContextMenuRenderer: React.FC = () => {
  const { menuState, hideContextMenu } = useContextMenu();

  if (!menuState.isOpen) return null;

  return (
    <ContextMenu
      items={menuState.items}
      position={menuState.position}
      onClose={hideContextMenu}
    />
  );
};

// Main Desktop Environment
const DesktopEnvironment: React.FC = () => {
  const { hasInitialRoute } = useRouting();
  const [phase, setPhase] = useState<'login' | 'boot' | 'desktop'>(
    hasInitialRoute ? 'desktop' : 'login'
  );

  const handleLogin = useCallback(() => {
    setPhase('boot');
  }, []);

  const handleBootComplete = useCallback(() => {
    setPhase('desktop');
  }, []);

  return (
    <>
      {phase === 'login' && <LoginScreen onLogin={handleLogin} />}
      {phase === 'boot' && <BootScreen onBootComplete={handleBootComplete} />}
      {phase === 'desktop' && (
        <>
          <Desktop />
          <WindowManager />
          <Taskbar />
          <CRTEffect />
          <ContextMenuRenderer />
        </>
      )}
    </>
  );
};

// Root App with all providers
const App: React.FC = () => {
  return (
    <SettingsProvider>
      <SoundProvider>
        <LoadingProvider>
          <DesktopIconsProvider>
            <WindowProvider>
              <RoutingProvider>
                <ContextMenuProvider>
                  <DesktopEnvironment />
                </ContextMenuProvider>
              </RoutingProvider>
            </WindowProvider>
          </DesktopIconsProvider>
        </LoadingProvider>
      </SoundProvider>
    </SettingsProvider>
  );
};

export default App;
